import api from './api';
import {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  User
} from '../types/api';
import { AUTH_TOKEN_KEY, USER_STORAGE_KEY } from '../constants/storageKeys';

/**
 * Converte o usuário do formato do backend para o frontend
 * Backend usa 'document', frontend usa 'cpf'
 */
const adaptUserFromBackend = (backendUser: unknown): User => {
  const obj = backendUser as Record<string, unknown>;
  const document = obj?.document as string | undefined;
  const cpf = (obj?.cpf as string | undefined) || document || '';
  const addresses = Array.isArray(obj?.addresses)
    ? (obj.addresses as User['addresses'])
    : [];
  const birthDateValue = obj?.birthDate;
  const birthDate =
    birthDateValue instanceof Date
      ? birthDateValue.toISOString().split('T')[0]
      : typeof birthDateValue === 'string' && birthDateValue.length > 0
        ? birthDateValue
        : undefined;

  return {
    ...(backendUser as User),
    cpf,
    addresses,
    birthDate,
  };
};

/**
 * Serviço de Autenticação
 */
const authService = {
  /**
   * Faz login do usuário
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await api.post<{ success: boolean; data: LoginResponse }>('/auth/login', credentials);
    
    // Adaptar usuário do backend para frontend
    const adaptedUser = adaptUserFromBackend(response.data.data.user);
    
    // Salvar token e usuário no localStorage
    if (response.data.data.token) {
      localStorage.setItem(AUTH_TOKEN_KEY, response.data.data.token);
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(adaptedUser));
    }
    
    return {
      user: adaptedUser,
      token: response.data.data.token,
    };
  },

  /**
   * Registra novo usuário
   */
  async register(userData: RegisterRequest): Promise<RegisterResponse> {
    // Transformar cpf para document (nome esperado pelo backend)
    const payload = {
      name: userData.name,
      email: userData.email,
      document: userData.cpf,
      password: userData.password,
      phone: userData.phone,
      birthDate: userData.birthDate,
    };
    
    const response = await api.post<{ success: boolean; data: RegisterResponse }>('/auth/register', payload);
    
    // Adaptar usuário do backend para frontend
    const adaptedUser = adaptUserFromBackend(response.data.data.user);
    
    // Salvar token e usuário no localStorage
    if (response.data.data.token) {
      localStorage.setItem(AUTH_TOKEN_KEY, response.data.data.token);
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(adaptedUser));
    }
    
    return {
      user: adaptedUser,
      token: response.data.data.token,
    };
  },

  /**
   * Faz logout do usuário
   */
  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } finally {
      // Limpar dados locais independente do resultado
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem(USER_STORAGE_KEY);
    }
  },

  /**
   * Retorna dados do usuário autenticado
   */
  async getMe(): Promise<{ user: User }> {
    const response = await api.get<{ success: boolean; data: { user: User } }>('/auth/me');
    
    // Adaptar usuário do backend para frontend
    const adaptedUser = adaptUserFromBackend(response.data.data.user);
    
    // Atualizar dados do usuário no localStorage
    if (response.data.data.user) {
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(adaptedUser));
    }
    
    return { user: adaptedUser };
  },

  /**
   * Verifica se o usuário está autenticado
   */
  isAuthenticated(): boolean {
    return !!localStorage.getItem(AUTH_TOKEN_KEY);
  },

  /**
   * Retorna o token de autenticação
   */
  getToken(): string | null {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  },

  /**
   * Retorna o usuário logado do localStorage
   */
  getCurrentUser(): User | null {
    const userStr = localStorage.getItem(USER_STORAGE_KEY);
    return userStr ? JSON.parse(userStr) : null;
  },

  /**
   * Solicita recuperação de senha
   */
  async forgotPassword(email: string): Promise<{ success: boolean; message?: string }> {
    const response = await api.post<{ success: boolean; data: { message: string } }>('/auth/forgot-password', {
      email
    });
    return {
      success: response.data.success,
      message: response.data.data?.message
    };
  },

  /**
   * Valida o token de redefinição de senha
   */
  async validateResetToken(token: string): Promise<{ success: boolean; valid?: boolean; message?: string }> {
    try {
      const response = await api.get<{ success: boolean; data: { valid: boolean; message: string } }>('/auth/validate-reset-token', {
        params: { token }
      });
      return {
        success: response.data.success,
        valid: response.data.data?.valid,
        message: response.data.data?.message
      };
    } catch (error: unknown) {
      const msg = error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
        : undefined
      return {
        success: false,
        valid: false,
        message: msg || 'Token inválido ou expirado'
      };
    }
  },

  /**
   * Redefine a senha usando o token
   */
  async resetPassword(token: string, newPassword: string): Promise<{ success: boolean; message?: string }> {
    const response = await api.post<{ success: boolean; data: { message: string } }>('/auth/reset-password', {
      token,
      newPassword
    });
    return {
      success: response.data.success,
      message: response.data.data?.message
    };
  },
};

export default authService;

